import type { Edge } from '@xyflow/react';

import { applyAutoLayout } from '@/components/fluxograma/autoLayout';
import type { OrgMvpCardData, OrgMvpNode } from '@/components/organograma/organogramaTypes';
import type { ImportGrupo } from './organogramaImport';

const DEPARTAMENTO_COLOR = '#059669';
const PESSOA_COLOR = '#2563EB';
const VAGA_ABERTA_COLOR = '#D97706';

let counter = 0;
const genId = (prefix: string) => {
  counter += 1;
  return `import_${prefix}_${Date.now().toString(36)}_${counter}`;
};

function cardNode(id: string, data: OrgMvpCardData): OrgMvpNode {
  return {
    id,
    type: 'card',
    position: { x: 0, y: 0 },
    width: 220,
    height: 118,
    zIndex: 10,
    style: { width: 220, height: 118 },
    data,
  };
}

function edgeBetween(sourceId: string, targetId: string, color: string): Edge {
  return {
    id: genId('edge'),
    source: sourceId,
    target: targetId,
    type: 'smoothstep',
    style: { stroke: color, strokeWidth: 2.2 },
    data: { tipo: 'direta' },
  };
}

/**
 * Converte os grupos interpretados do import em nodes/edges do React Flow,
 * já com auto-layout aplicado (isolado, sem tocar no canvas existente).
 */
export function buildNodesAndEdgesFromGrupos(grupos: ImportGrupo[]): { nodes: OrgMvpNode[]; edges: Edge[] } {
  const nodes: OrgMvpNode[] = [];
  const edges: Edge[] = [];

  for (const grupo of grupos) {
    const grupoId = genId('grupo');
    nodes.push(
      cardNode(grupoId, {
        kind: 'department',
        label: grupo.titulo || 'Grupo importado',
        subtitle: grupo.coordenador ? `Coordenador: ${grupo.coordenador}` : '',
        description: '',
        color: DEPARTAMENTO_COLOR,
        textColor: '#0F172A',
        figure: { type: 'catalog', key: 'building' },
      }),
    );

    for (const departamento of grupo.departamentos) {
      const departamentoId = genId('dept');
      nodes.push(
        cardNode(departamentoId, {
          kind: 'department',
          label: departamento.nome || 'Departamento',
          subtitle: '',
          description: '',
          color: DEPARTAMENTO_COLOR,
          textColor: '#0F172A',
          figure: { type: 'catalog', key: 'team' },
        }),
      );
      edges.push(edgeBetween(grupoId, departamentoId, DEPARTAMENTO_COLOR));

      for (const pessoa of departamento.pessoas) {
        const pessoaId = genId('pessoa');
        const color = pessoa.vagaAberta ? VAGA_ABERTA_COLOR : PESSOA_COLOR;
        nodes.push(
          cardNode(pessoaId, {
            kind: 'person',
            label: pessoa.nome,
            subtitle: pessoa.cargo,
            description: pessoa.vagaAberta ? 'Vaga aberta (importado)' : '',
            sourceType: 'ORG_ONLY',
            usuarioId: null,
            departamentoId: null,
            color,
            textColor: '#0F172A',
            figure: { type: 'catalog', key: 'person' },
          }),
        );
        edges.push(edgeBetween(departamentoId, pessoaId, color));
      }
    }
  }

  const { nodes: laidOut } = applyAutoLayout(nodes, edges, { direction: 'TB', nodeWidth: 220, nodeHeight: 118 });
  return { nodes: laidOut as OrgMvpNode[], edges };
}

/** Desloca o subgrafo importado para a direita do conteúdo já existente no canvas, sem alterá-lo. */
export function offsetImportedNodes(nodes: OrgMvpNode[], existingNodes: OrgMvpNode[]): OrgMvpNode[] {
  if (existingNodes.length === 0) return nodes;
  const maxRight = existingNodes.reduce((max, node) => {
    const width = typeof node.width === 'number' ? node.width : 220;
    return Math.max(max, node.position.x + width);
  }, 0);
  const offsetX = maxRight + 160;
  return nodes.map((node) => ({ ...node, position: { x: node.position.x + offsetX, y: node.position.y } }));
}
